import { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import {
  Account,
  DomainsResponse,
  AssignedRolesResponse,
  type AssignedDomainResponse,
  type AssignedDomainsResponse,
  type DomainAssignee,
  type EffectiveRolesResponse
} from '@percent/domain/iam'
import { iamServiceCapabilities } from './iamService.types'

export const iamService = ({ percentClient }: iamServiceCapabilities) => ({
  getRolesForPartner: ({ partnerId }: { partnerId: string }) =>
    percentClient.get(`/v1/admin/iam/partner/${partnerId}/roles`),
  setRoleForAccount: ({ accountId, role }: { accountId: string; role: string }) =>
    percentClient.patch(`/v1/admin/iam/account/${accountId}/role`, { roleId: role }),
  getAccountDetail: ({ accountId }: { accountId: string }) =>
    percentClient.get<PBPGetResponse<Account>>(`/v1/admin/iam/accounts/${accountId}`),
  // The domains the viewer can view roles on, each saying whether they can also set them
  getDomains: () => percentClient.get<PBPGetResponse<DomainsResponse>>('/v1/admin/authorisation/domains'),
  getAssignedRoles: ({ accountId }: { accountId: string }) =>
    percentClient.get<PBPGetResponse<AssignedRolesResponse>>(
      `/v1/admin/authorisation/domains/accounts/${accountId}/domains`
    ),
  getAssignedDomains: ({
    accountId,
    consistencyLevel
  }: {
    accountId: string
    consistencyLevel: 'strong' | 'eventual'
  }) =>
    percentClient.get<PBPGetResponse<AssignedDomainsResponse>>(
      `/v1/admin/authorisation/domains/accounts/${accountId}/assigned-domains`,
      { params: { consistencyLevel } }
    ),
  getGroupAssignedDomains: ({
    groupId,
    consistencyLevel
  }: {
    groupId: string
    consistencyLevel: 'strong' | 'eventual'
  }) =>
    percentClient.get<PBPGetResponse<AssignedDomainsResponse>>(
      `/v1/admin/authorisation/domains/groups/${groupId}/assigned-domains`,
      { params: { consistencyLevel } }
    ),
  // The roles an account or group holds and why; eventual consistency is the
  // endpoint's default and enough for showing them.
  getEffectiveRoles: ({
    subject,
    id,
    consistencyLevel
  }: {
    subject: 'accounts' | 'groups'
    id: string
    consistencyLevel: 'strong' | 'eventual'
  }) =>
    percentClient.get<PBPGetResponse<EffectiveRolesResponse>>(
      `/v1/admin/authorisation/${subject}/${id}/effective-roles`,
      { params: { consistencyLevel } }
    ),
  // Cursor-paginated (next token only) and returns no totalResults
  getDomainAssignees: ({ domainId, next, pageSize }: { domainId: string; next?: string; pageSize?: number }) =>
    percentClient.get<PBPListResponse<DomainAssignee>>(`/v1/admin/authorisation/domains/${domainId}/assignees`, {
      params: { next, pageSize }
    }),
  assignAccountRole: ({ memberId, role, domainId }: { memberId: string; role: string; domainId: string }) =>
    percentClient.put<PBPGetResponse<AssignedDomainResponse>>(
      `/v1/admin/authorisation/domains/${domainId}/assignees/${memberId}`,
      { roleId: role }
    ),
  unassignAccountRole: ({ memberId, role, domainId }: { memberId: string; role: string; domainId: string }) =>
    percentClient.delete<PBPGetResponse<AssignedDomainResponse>>(
      `/v1/admin/authorisation/domains/${domainId}/assignees/${memberId}`,
      {
        data: { roleId: role }
      }
    ),
  getPermissions: () => percentClient.get('/v1/admin/authorisation/permissions')
})
